import React from 'react'
import { AlertCircle, Download, Loader2, Trash2 } from 'lucide-react'
import { formatFileSize, getFileIcon } from '../hooks/utils'
import type { AttachmentListProps } from '../types'

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  uploadProgress = [],
  onDownload,
  onDelete,
  readOnly = false,
}) => {
  const [deletingId, setDeletingId] = React.useState<string | null>(null)

  const handleDownload = async (attachment: any) => {
    try {
      const url = await onDownload(attachment.id)
      window.open(url, '_blank')
    } catch (error) {
      // Error handling
      alert('Failed to download file')
    }
  }

  const handleDelete = async (attachmentId: string) => {
    if (!onDelete) return
    if (!confirm('Are you sure you want to delete this attachment?')) return

    setDeletingId(attachmentId)
    try {
      await onDelete(attachmentId)
    } catch (error) {
      // Error handling
      alert('Failed to delete attachment')
    } finally {
      setDeletingId(null)
    }
  }

  if (attachments.length === 0 && uploadProgress.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {/* Upload progress */}
      {uploadProgress.map((progress) => (
        <div
          key={progress.attachmentId}
          className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border"
        >
          <div className="flex-shrink-0">
            {progress.status === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {progress.fileName}
            </p>
            <div className="mt-1">
              <div className="w-full bg-border rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    progress.status === 'error' ? 'bg-red-500' : 'bg-primary'
                  }`}
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {progress.status === 'error'
                  ? progress.error || 'Upload failed'
                  : progress.status === 'confirming'
                    ? 'Confirming upload...'
                    : `Uploading... ${progress.progress}%`}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Attachments list */}
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border hover:border-border/80 transition-colors"
        >
          <div className="flex-shrink-0">
            {getFileIcon(attachment.file_type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {attachment.file_name}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground">
                {formatFileSize(attachment.file_size_bytes)}
              </p>
              {attachment.description && (
                <>
                  <span className="text-muted-foreground/70">•</span>
                  <p className="text-xs text-muted-foreground truncate">
                    {attachment.description}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDownload(attachment)}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-secondary rounded transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
            {!readOnly && onDelete && (
              <button
                onClick={() => handleDelete(attachment.id)}
                disabled={deletingId === attachment.id}
                className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors disabled:opacity-50"
                title="Delete"
              >
                {deletingId === attachment.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
